/*
  LIORIVE — mobile sticky Add to Cart. Vanilla JS, no dependencies.
  It never owns any state: label, disabled state and price are always read back from the
  native Dawn buy button / price node, which product-info.js and product-form.js keep up to date
  (variant change, sold out, unavailable, loading). Submission goes through form.requestSubmit(),
  so Dawn's own product-form handler, quantity input, cart drawer and error handling run unchanged.
  Elements are looked up lazily by id so a re-rendered product-info never leaves stale references.
*/
if (!customElements.get('liorive-sticky-atc')) {
  customElements.define(
    'liorive-sticky-atc',
    class LioriveStickyAtc extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-liorive-btn]');
        this.priceOut = this.querySelector('[data-liorive-price]');
        this.root = this.closest('product-info') || document;
        if (!this.btn || !this.mainButton) return; // sold-out markup has no submit button id: stay hidden

        this.btn.addEventListener('click', this.onClick);
        this.sync();

        this.mutations = new MutationObserver((records) => {
          // Our own writes in sync() land inside the observed subtree: ignore them, or every
          // sync would schedule the next one and the loop would run on every frame.
          if (records.every((r) => this.contains(r.target))) return;
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.sync());
        });
        this.mutations.observe(this.root, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        const target = this.mainButton.closest('product-form') || this.mainButton;
        this.pos = 'visible';
        this.hiddenByFooter = false;
        if ('IntersectionObserver' in window) {
          this.intersections = new IntersectionObserver(() => {
            this.readPosition();
            this.applyVisibility();
          });
          this.intersections.observe(target);
        }

        // Also hide once the visitor reaches the end of the page: the sticky bar must not sit on
        // top of the footer (links, payment icons) once there is nothing left to scroll to.
        this.onScroll = () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => this.checkFooter());
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.checkFooter();
      }

      disconnectedCallback() {
        this.mutations?.disconnect();
        this.intersections?.disconnect();
        cancelAnimationFrame(this.frame);
        cancelAnimationFrame(this.scrollFrame);
        this.btn?.removeEventListener('click', this.onClick);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      checkFooter() {
        const threshold = 24; // px of slack before the true bottom of the document
        const atBottom =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - threshold;
        this.hiddenByFooter = atBottom;
        // The observer only fires when the button crosses the viewport edge. A jump from below the
        // button straight back to the top (scroll restore, «back to top», anchor) never crosses it, and
        // the bar stayed over the price. Re-read the position on every scroll as well.
        this.readPosition();
        this.applyVisibility();
      }

      // LIORIVE (2026-10-11): the bar is shown whenever the native button is out of view, also before
      // it is reached (on a phone it sits below the gallery). Until then, for a piece with several
      // colours, the bar invites to choose the colour first and brings the colour bands into view;
      // once the native button has been scrolled past, it adds to the cart as before.
      readPosition() {
        const target = this.mainButton?.closest('product-form') || this.mainButton;
        if (!target) return;
        const r = target.getBoundingClientRect();
        this.pos = r.top > window.innerHeight ? 'below' : r.bottom < 0 ? 'above' : 'visible';
      }

      get picker() {
        const fieldset = this.root.querySelector('variant-selects fieldset');
        return fieldset && fieldset.querySelectorAll('input[type="radio"]').length > 1 ? fieldset : null;
      }

      get choosing() {
        return this.pos === 'below' && !!this.picker;
      }

      applyVisibility() {
        this.hidden = this.pos === 'visible' || this.hiddenByFooter;
        this.sync();
        // The Inbox chat button is lifted above this bar in CSS (liorive-refine.css), from its hidden state.
      }

      get mainButton() {
        return document.getElementById(this.dataset.button);
      }

      get form() {
        return document.getElementById(this.dataset.form);
      }

      onClick = () => {
        const main = this.mainButton;
        const form = this.form;
        if (!main || !form) return;
        if (this.choosing) {
          const picker = this.picker;
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const y = window.scrollY;
          picker.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
          picker.querySelector('input:checked, input')?.focus({ preventScroll: true });
          // Smooth scrolling can be ignored (some in-app browsers): jump if the page has not moved.
          setTimeout(() => {
            if (window.scrollY === y) picker.scrollIntoView({ behavior: 'auto', block: 'center' });
          }, 600);
          return;
        }
        if (main.disabled || main.getAttribute('aria-disabled') === 'true') return;
        form.requestSubmit(main);
      };

      sync() {
        const main = this.mainButton;
        if (!main) {
          this.hidden = true;
          return;
        }
        const choosing = this.choosing;
        const legend = (this.picker?.querySelector('legend')?.textContent || '').replace(/[:\s]+$/, '').trim().toLowerCase();
        const label = choosing
          ? legend.startsWith('couleur') ? 'Choisir la couleur' : 'Choisir une option'
          : (main.querySelector('span')?.textContent || '').trim();
        if (label && this.btn.textContent !== label) this.btn.textContent = label;
        const disabled = !choosing && (main.disabled || main.getAttribute('aria-disabled') === 'true');
        const off = !choosing && main.disabled;
        if (this.btn.disabled !== off) this.btn.disabled = off; // sold out / unavailable: mirror the native state
        if (this.btn.getAttribute('aria-disabled') !== String(disabled)) this.btn.setAttribute('aria-disabled', String(disabled));
        this.btn.classList.toggle('loading', main.classList.contains('loading'));

        const priceEl = document.getElementById(this.dataset.price);
        if (priceEl && this.priceOut) {
          const onSale = priceEl.querySelector('.price--on-sale');
          const node = onSale
            ? priceEl.querySelector('.price-item--sale')
            : priceEl.querySelector('.price-item--regular');
          const price = node ? node.textContent.trim() : '';
          if (this.priceOut.textContent !== price) this.priceOut.textContent = price;
        }
      }
    },
  );
}
